/**
 * Pie Chart component (used for PAGE Funding)
 */

import React from "react";
import parse from "html-react-parser";
import { kebabCase } from "lodash";
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement } from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import CopyLinkButton from "./copy-link-button";

ChartJS.register(ArcElement, Tooltip, Legend);
ChartJS.register(ChartDataLabels);
ChartJS.register(CategoryScale);
ChartJS.register(LinearScale);
ChartJS.register(BarElement);


function abbreviateFunds(value) {
  // Nine Zeroes for Billions
  return Math.abs(Number(value)) >= 1.0e9
    ? Math.round((Math.abs(Number(value)) / 1.0e9) * 100) / 100 + "B"
    : // Six Zeroes for Millions
    Math.abs(Number(value)) >= 1.0e6
      ? Math.round((Math.abs(Number(value)) / 1.0e6) * 100) / 100 + "M"
      : // Three Zeroes for Thousands
      Math.abs(Number(value)) >= 1.0e3
        ? Math.round((Math.abs(Number(value)) / 1.0e3) * 100) / 100 + "K"
        : Math.abs(Number(value));
}

const PieChart = ({ props, annualReport }) => {
  const prependValue = props?.prependValues || "";
  const appendValue = props?.appendValues || "";

  const options = {
    animation: false,
    maintainAspectRatio: false,
    plugins: {
      legend: props.chartType === "bar" ? { display: false, maxHeight: 0, } : {
        display: true,
        labels: {
          fontSize: 16,
          fontFamily: '"Montserrat", "sans-serif"',
          fontColor: "#111827",
        },
      },
      datalabels: props.chartType === "bar" ? null : {
        formatter: (value, ctx) => {
          let sum = 0;
          let dataArr = ctx.chart.data.datasets[0].data;
          dataArr.map((data) => {
            sum += data;
            return true;
          });
          let percentage = ((value * 100) / sum).toFixed(0);
          return percentage > 2 ? `${percentage}%` : "";
        },
        color: "#111827",
        offset: 0,
        align: "end",
      },
    },
    scales: props.chartType === "bar" ? {
      y: {
        title: {
          text: appendValue,
          title: appendValue,
          display: true,
          padding: 20,
        },
      },
    } : null,
    tooltips: {
      bodyFontSize: 16,
      callbacks: {
        label: function (tooltipItem, data) {
          return data.labels[tooltipItem.index] || "";
        },
      },
    },
  };

  const getData = (props) => {
    let data = {
      labels: [],
      datasets: [
        {
          data: [],
          backgroundColor: [],
          hoverBackgroundColor: [],
        },
      ],
    };
    for (let index in props.data) {
      const dataItem = props.data[index];
      data.labels.push(
        `${dataItem.label}${props.chartType === "bar" ? "" : " - " + prependValue + abbreviateFunds(dataItem.value) + appendValue}`
      );
      data.datasets[0].data.push(dataItem.value);
      data.datasets[0].backgroundColor.push(dataItem.backgroundColor);
      data.datasets[0].hoverBackgroundColor.push(dataItem.hoverBackgroundColor);
    }
    return data;
  };

  return (
    <section
      key={props.heading || props.includeInStickyMenu?.menuTitle}
      className={`section ${annualReport === "2024" ? "p-section relative" : " m-section"}`}
    >
      {annualReport === "2024" ? (
        <div className="absolute top-0 left-0 w-full h-full bg-circles-blue opacity-15 bg-cover print:hidden" aria-hidden />) : null
      }
      <div className="relative container">
        <div className={`relative mx-auto lg:w-${props.contentWidth}/12 mb-6`}>
          <div className="group-copy-link group flex items-start justify-center">
            {props.heading ? (
              <>
                <h2
                  id={kebabCase(props.heading).replace(/[0-9]/g, "")}
                  className={`anchor-heading text-center mb-6 ${annualReport === "2024" ? "h3 text-ar24-blue-900" : ""}`}
                  data-sal="slide-up"
                  data-sal-delay="300"
                >
                  {parse(props.heading)}
                </h2>
                <CopyLinkButton heading={props.heading} />
              </>
            ) : props.includeInStickyMenu?.menuTitle ? (
              <div id={kebabCase(props.includeInStickyMenu.menuTitle) || ""}>
                <CopyLinkButton heading={props.includeInStickyMenu.menuTitle} />
              </div>
            ) : null}
          </div>
          {props.leadContent ? (
            <div
              className="mt-6 text-center lead-content parsed-content"
              data-sal="fade-in"
              data-sal-delay="300"
            >
              {parse(props.leadContent)}
            </div>
          ) : null}
        </div>
        {props.data ? (
          <>
            <div aria-hidden={true} data-sal="fade-in" data-sal-delay="300">
              {props.chartType === "bar" ?
                <Bar data={getData(props)} options={options} height="560" />
                :
                <Doughnut data={getData(props)} options={options} height="560" />
              }
            </div>
            <div className="sr-only">
              <table>
                <caption>{props.heading}</caption>
                {props.data?.map((dataItem) => {
                  return (
                    <tr>
                      <th scope="row">{dataItem.label}</th>
                      <td>
                        {dataItem.value
                          ? "$" + abbreviateFunds(dataItem.value)
                          : ""}
                      </td>
                    </tr>
                  );
                })}
              </table>
            </div>
          </>
        ) : null}
        <div className={`relative mx-auto lg:w-${props.contentWidth}/12`}>
          {props.content ? (
            <div
              className="mt-6 parsed-content"
              data-sal="fade-in"
              data-sal-delay="300"
            >
              {parse(props.content)}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
};

export default PieChart;
